<script lang="ts">
	import { createBubbler, stopPropagation } from 'svelte/legacy';

	const bubble = createBubbler();
	import { createPopover, melt } from '@melt-ui/svelte';
	import IconQuestion from '$lib/assets/svg/icons/question.svg?component';
	import { fade } from 'svelte/transition';
	import type { FloatingConfig } from '@melt-ui/svelte/internal/actions';
	import { closeOnLeaveAction } from '$lib/utils';

	interface Props {
		title?: string | null;
		text?: string | null;
		defaultOpen?: boolean;
		positioning?: FloatingConfig;
		triggerSlot?: import('svelte').Snippet;
		children?: import('svelte').Snippet;
	}

	let {
		title = null,
		text = null,
		defaultOpen = false,
		positioning = null,
		triggerSlot,
		children
	}: Props = $props();

	const {
		elements: { trigger, content, arrow },
		states: { open }
	} = createPopover({
		positioning: positioning || {
			placement: 'top-start'
		},
		forceVisible: true,
		portal: undefined,
		closeOnOutsideClick: true,
		defaultOpen
	});
</script>

{#if triggerSlot}
	<span use:melt={$trigger} class="Tooltip__trigger" use:closeOnLeaveAction={open}>
		{@render triggerSlot?.()}
	</span>
{:else}
	<button
		type="button"
		class="Tooltip__button"
		onpointerdown={(e) => {
			e.stopPropagation();
			// Hack to hide all the currently opened tooltips when clicking on the button
			// Should be onpointerdown as we need to call it earlier than MeltUI's use:melt for the current tooltip
			Array.from(document.getElementsByClassName('Tooltip__dialog')).forEach((element) => {
				(element as HTMLElement).style.display = 'none';
			});
			bubble('click');
		}}
		use:melt={$trigger}
		aria-label="Tooltip"
		aria-expanded={$open}
		use:closeOnLeaveAction={open}
	>
		<IconQuestion></IconQuestion>
	</button>
{/if}

{#if $open && (title || text || children)}
	<div
		role="presentation"
		onclick={stopPropagation(bubble('click'))}
		use:melt={$content}
		transition:fade={{ duration: 100 }}
		class="Tooltip__dialog"
	>
		<div use:melt={$arrow}></div>
		{#if title}
			<h4 class="Tooltip__title">{title}</h4>
		{/if}
		{#if text}
			<p class="Tooltip__text">{text}</p>
		{/if}
		{#if children}
			<div class="Tooltip__slot">
				{@render children?.()}
			</div>
		{/if}
	</div>
{/if}

<style lang="scss" src="./Tooltip.scss"></style>
